import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { ROLES } from "../lib/roles";
import Recorder from "../components/Recorder";
import NoteEditor from "../components/NoteEditor";
import HistorySidebar from "../components/HistorySidebar";
import OversightView from "../components/OversightView";
import ConsumerManager from "../components/ConsumerManager";
import PcpCreator from "../components/PcpCreator";
import NoteGuide from "../components/NoteGuide";
import WeeklyBuilder from "../components/WeeklyBuilder";
import LibraryManager from "../components/LibraryManager";
import RenewalsView from "../components/RenewalsView";
import QpSummary from "../components/QpSummary";
import ConsumerCombobox from "../components/ConsumerCombobox";
import StaffDashboard from "../components/StaffDashboard";
import QpDashboard from "../components/QpDashboard";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import GuidedNoteInput, { buildGuidedRaw } from "../components/GuidedNoteInput";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Mic, LogOut, Sparkles, Loader2, Wand2, PenLine, Users, ShieldCheck, FileHeart, Target, FilePlus2, CalendarDays, Library, CalendarClock, LayoutDashboard, UserPlus, Upload, BarChart3 } from "lucide-react";

const EMPTY = {
  date_of_service: "", service_name: "Psychosocial Rehabilitation (PSR)",
  type_of_contact: "", place_of_service: "", duration_minutes: "", units: "",
  member_name: "", member_id: "", staff_name: "", staff_credentials: "",
  goals_addressed: "", interventions: "", member_response: "", progress: "", plan: "",
};

export default function Workspace() {
  const { user, logout } = useAuth();
  const roleMeta = ROLES[user?.role] || ROLES.pp;
  const canWrite = roleMeta.can_write;
  const canOversee = roleMeta.can_oversee;

  const [raw, setRaw] = useState("");
  const [inputMode, setInputMode] = useState("guided"); // guided | free
  const [guided, setGuided] = useState({});
  const [plannedUsed, setPlannedUsed] = useState([]);
  const [lastNote, setLastNote] = useState(null);
  const [repeat, setRepeat] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [structured, setStructured] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [notes, setNotes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [showTimestamp, setShowTimestamp] = useState(true);
  const [createdAt, setCreatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [signature, setSignature] = useState(null);
  const [weekOf, setWeekOf] = useState(null);
  const [alignment, setAlignment] = useState(null);
  // "dashboard" is every writer's landing page; "team" for oversight
  const [view, setView] = useState(canWrite ? "dashboard" : "review");
  // consumers (PCP source of truth)
  const [consumers, setConsumers] = useState([]);
  const [consumerId, setConsumerId] = useState("");
  const [recentIds, setRecentIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("psr_recent_consumers") || "[]"); } catch { return []; }
  });
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [goalsText, setGoalsText] = useState("");
  const [interventionsText, setInterventionsText] = useState("");
  const [pcpUploading, setPcpUploading] = useState(false);
  const [saveClientOpen, setSaveClientOpen] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  const [newClientId, setNewClientId] = useState("");
  const [savingClient, setSavingClient] = useState(false);

  const loadNotes = async () => {
    try {
      const { data } = await api.get("/notes");
      setNotes(data);
    } catch (e) {
      /* ignore */
    }
  };

  const loadConsumers = async () => {
    try {
      const { data } = await api.get("/consumers");
      setConsumers(data);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    if (canWrite) {
      loadNotes();
      loadConsumers();
    }
  }, [canWrite]);

  const selectedConsumer = consumers.find((c) => c.id === consumerId) || null;

  const pickConsumer = (cid) => {
    setConsumerId(cid);
    setSelectedGoals([]);
    const c = consumers.find((x) => x.id === cid);
    setGoalsText(c ? (c.goals || []).filter(Boolean).join("\n") : "");
    setInterventionsText(c ? (c.interventions || []).filter(Boolean).join("\n") : "");
    if (cid) {
      setRecentIds((prev) => {
        const next = [cid, ...prev.filter((id) => id !== cid)].slice(0, 5);
        try { localStorage.setItem("psr_recent_consumers", JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
    }
  };

  // start a note for a dashboard consumer, optionally pre-loaded with this week's planned interventions
  const startNoteFor = (c, goals, interventions) => {
    setStructured(null);
    setRaw("");
    setActiveId(null);
    setSaved(false);
    setSignature(null);
    setWeekOf(null);
    setAlignment(null);
    setRepeat(null);
    setGuided({});
    pickConsumer(c.id);
    if (goals?.length) setGoalsText(goals.join("\n"));
    if (interventions?.length) setInterventionsText(interventions.join("\n"));
    setInputMode("guided");
    setTimeout(() => setPlannedUsed(interventions || []), 400);
    setView("write");
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (interventions?.length) toast.success("Loaded this week's interventions — now just tell me what happened.");
  };

  const saveAsClient = async () => {
    if (!newClientName.trim()) {
      toast.info("Please enter the client's name.");
      return;
    }
    setSavingClient(true);
    try {
      const goals = goalsText.split("\n").map((s) => s.trim()).filter(Boolean);
      const interventions = interventionsText.split("\n").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/consumers", {
        name: newClientName.trim(),
        member_id: newClientId.trim(),
        goals,
        interventions,
      });
      await loadConsumers();
      setConsumerId(data.id);
      setSaveClientOpen(false);
      setNewClientName("");
      setNewClientId("");
      toast.success(`Saved ${data.name} — pick them from the dropdown next time.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingClient(false);
    }
  };

  const handlePcpUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPcpUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: ext } = await api.post("/pcp/extract-text", fd);
      const { data: g } = await api.post("/pcp/extract-goals", { raw_input: ext.text });
      if (g.goals?.length) setGoalsText(g.goals.join("\n"));
      if (g.interventions?.length) setInterventionsText(g.interventions.join("\n"));
      if (g.goals?.length || g.interventions?.length) {
        toast.success("PCP loaded — your note will now match these goals & interventions.");
      } else {
        toast.info("Read the file, but couldn't find clear goals. Try pasting them instead.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read that PCP file.");
    } finally {
      setPcpUploading(false);
    }
  };

  const generate = async () => {
    const text = inputMode === "guided" ? buildGuidedRaw(guided, plannedUsed, lastNote?.plan) : raw;
    if (!text.trim()) {
      toast.info(inputMode === "guided" ? "Answer at least the first question — talk or type." : "Tell me about the session first — talk or type below.");
      return;
    }
    if (inputMode === "guided") setRaw(text);
    setGenerating(true);
    try {
      const { data } = await api.post("/notes/generate", { raw_input: text, consumer_id: consumerId || null, selected_goals: selectedGoals, pasted_goals: goalsText, pasted_interventions: interventionsText });
      setStructured(data.structured);
      setActiveId(null);
      setCreatedAt(new Date().toISOString());
      setSaved(false);
      setSignature(null);
      setWeekOf(null);
      setAlignment(data.alignment || null);
      setRepeat(data.repeat?.repeat ? data.repeat : null);
      toast.success(data.repeat?.repeat ? "Note drafted — but the interventions look like last week's. Fix them before saving." : "Your note is ready — review it below.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const refreshInterventions = async () => {
    if (!structured) return;
    setRefreshing(true);
    try {
      const { data } = await api.post("/notes/refresh-interventions", { consumer_id: consumerId || null, raw_input: raw, structured, exclude_id: activeId });
      setStructured({ ...structured, interventions: data.interventions, plan: data.plan });
      setRepeat(data.repeat?.repeat ? data.repeat : null);
      setSaved(false);
      toast.success(data.repeat?.repeat ? "Rewritten — still close to last week; add more detail about what you did." : "Interventions rewritten for this week.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const compiledWeekly = (structuredNote, cid, weekStart, align, rep) => {
    setStructured(structuredNote);
    setRepeat(rep?.repeat ? rep : null);
    setConsumerId(cid || "");
    setActiveId(null);
    setCreatedAt(new Date().toISOString());
    setSaved(false);
    setSignature(null);
    setWeekOf(weekStart || null);
    setAlignment(align || null);
    setRaw("");
    setView("write");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    setSaving(true);
    const goalFlagged = alignment?.status === "none";
    try {
      if (consumerId && structured) {
        const { data: chk } = await api.post("/notes/repeat-check", { consumer_id: consumerId, interventions: structured.interventions, exclude_id: activeId });
        if (chk.repeat) {
          setRepeat(chk);
          toast.error("These interventions match last week's note. Interventions must change every week.");
          setSaving(false);
          return;
        }
        setRepeat(null);
      }
      if (activeId) {
        await api.put(`/notes/${activeId}`, {
          structured, show_timestamp: showTimestamp, status: "finalized", signature, goal_flagged: goalFlagged,
        });
      } else {
        const { data } = await api.post("/notes", {
          raw_input: raw, structured, show_timestamp: showTimestamp, status: "finalized",
          consumer_id: consumerId || null, signature, goal_flagged: goalFlagged,
        });
        setActiveId(data.id);
        setCreatedAt(data.created_at);
      }
      setSaved(true);
      toast.success("Note saved!");
      await loadNotes();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const selectNote = (n) => {
    setStructured(n.structured);
    setRaw(n.raw_input || "");
    setActiveId(n.id);
    setShowTimestamp(n.show_timestamp !== false);
    setCreatedAt(n.created_at);
    setSignature(n.signature || null);
    setWeekOf(null);
    setAlignment(null);
    setRepeat(null);
    setSaved(true);
    setView("write");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const newNote = () => {
    setStructured(null);
    setRaw("");
    setGuided({});
    setPlannedUsed([]);
    setRepeat(null);
    setActiveId(null);
    setSaved(false);
    setCreatedAt(null);
    setSignature(null);
    setWeekOf(null);
    setAlignment(null);
    setGoalsText("");
    setInterventionsText("");
    setConsumerId("");
    setSelectedGoals([]);
    setView("write");
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/notes/${id}`);
      if (id === activeId) newNote();
      await loadNotes();
      toast.success("Note deleted.");
    } catch (e) {
      toast.error("Could not delete note.");
    }
  };

  const writingUI = (
    <>
      {/* input canvas */}
      <div className="bg-white border border-[#E2DFD8] rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 sm:px-10 pt-10 pb-8 flex flex-col items-center text-center border-b border-[#E2DFD8] bg-gradient-to-b from-white to-[#F9F9F6]">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-[#121814] max-w-xl leading-tight">
            {inputMode === "guided" ? "Three quick questions" : "Tell me about your session"}
          </h1>
          <p className="mt-3 text-[#7A8C82] max-w-md leading-relaxed">
            {inputMode === "guided"
              ? "Pick the consumer, tap Dictate on each question and just talk. I'll write the compliant weekly note — what you did, how they responded, and next week's plan."
              : "Just talk like you're telling a coworker what happened. Tap the button and speak — or type below. I'll write the proper note."}
          </p>
          <div className="mt-5 inline-flex p-1 rounded-full bg-[#EAE8E3] border border-[#E2DFD8]">
            <button data-testid="note-mode-guided" onClick={() => setInputMode("guided")} className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${inputMode === "guided" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"}`}>Guided questions</button>
            <button data-testid="note-mode-free" onClick={() => setInputMode("free")} className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${inputMode === "free" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"}`}>Just talk</button>
          </div>
          {inputMode === "free" && (
            <div className="mt-6">
              <Recorder
                disabled={generating}
                onTranscript={(t) => setRaw((r) => (r ? r + " " + t : t))}
              />
            </div>
          )}
        </div>

        <div className="p-6 sm:p-8">
          {/* consumer selector */}
          <div className="mb-5">
            <label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">
              Consumer (pulls in their PCP, goals & interventions)
            </label>
            <div className="mt-2 flex flex-col sm:flex-row gap-3 sm:items-center">
              <ConsumerCombobox
                consumers={consumers}
                value={consumerId}
                recentIds={recentIds}
                onChange={pickConsumer}
              />
              {consumers.length === 0 && (
                <span className="text-xs text-[#7A8C82]">
                  {canOversee ? "No consumers yet — create or upload a PCP to add one." : "No consumers assigned to you yet — ask your QP."}
                </span>
              )}
            </div>

            {selectedConsumer && (
              <div className="mt-3 rounded-xl border border-[#DCE5DF] bg-[#F4F6F3] p-4 fade-up" data-testid="consumer-reference">
                <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Target className="w-3.5 h-3.5" strokeWidth={1.5} /> {selectedConsumer.name}'s PCP — I'll align the note to this
                </p>
                {selectedConsumer.diagnosis && (
                  <p className="text-xs text-[#3A453F] mb-2"><span className="font-semibold">Diagnosis:</span> {selectedConsumer.diagnosis}</p>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider mb-1.5">
                      Goals — tap the one(s) you worked on
                    </p>
                    {(selectedConsumer.goals || []).length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {selectedConsumer.goals.map((g, i) => {
                          const on = selectedGoals.includes(g);
                          return (
                            <button
                              key={i}
                              data-testid={`goal-chip-${i}`}
                              onClick={() =>
                                setSelectedGoals((sg) => (on ? sg.filter((x) => x !== g) : [...sg, g]))
                              }
                              className={`text-left text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
                                on
                                  ? "bg-[#4A6B53] border-[#3D5A45] text-white"
                                  : "bg-white border-[#DCE5DF] text-[#3A453F] hover:border-[#4A6B53]"
                              }`}
                            >
                              {g}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-[#7A8C82]">None listed</p>
                    )}
                    {selectedGoals.length > 0 && (
                      <p className="text-[11px] text-[#4A6B53] mt-1.5" data-testid="selected-goals-count">
                        Targeting {selectedGoals.length} goal{selectedGoals.length === 1 ? "" : "s"} — leave unselected to let AI decide.
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider mb-1">Interventions</p>
                    <ul className="text-xs text-[#3A453F] space-y-0.5 list-disc list-inside">
                      {(selectedConsumer.interventions || []).length ? selectedConsumer.interventions.map((iv, i) => <li key={i}>{iv}</li>) : <li className="list-none text-[#7A8C82]">None listed</li>}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Goals & Interventions — pinned so they stay visible while dictating */}
          <div className="sticky top-4 z-20 mb-5 rounded-2xl border-2 border-[#4A6B53]/25 bg-[#F4F6F3] p-4 shadow-[0_4px_12px_rgba(74,107,83,0.08)]">
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-wider flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5" strokeWidth={1.5} /> Goals &amp; Interventions — paste, or upload the PCP, so your note matches
              </p>
              <div className="flex items-center gap-2">
                <label
                  data-testid="upload-pcp-label"
                  className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors cursor-pointer ${pcpUploading ? "bg-[#EAE8E3] border-[#DCE5DF] text-[#7A8C82]" : "bg-white border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53] hover:text-white"}`}
                >
                  {pcpUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />}
                  {pcpUploading ? "Reading PCP…" : "Upload PCP"}
                  <input data-testid="upload-pcp-input" type="file" accept=".pdf,.docx,.txt,.md,.png,.jpg,.jpeg" className="hidden" onChange={handlePcpUpload} disabled={pcpUploading} />
                </label>
                {!consumerId && (goalsText.trim() || interventionsText.trim()) && (
                  <button
                    data-testid="save-as-client-btn"
                    onClick={() => setSaveClientOpen(true)}
                    className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53] hover:text-white transition-colors"
                  >
                    <UserPlus className="w-3.5 h-3.5" strokeWidth={1.5} /> Save as a client
                  </button>
                )}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Goals</label>
                <Textarea
                  data-testid="paste-goals"
                  value={goalsText}
                  onChange={(e) => setGoalsText(e.target.value)}
                  rows={3}
                  placeholder="Paste the client's goals here — one per line."
                  className="mt-1.5 bg-white border-[#DCE5DF] text-sm leading-relaxed resize-y"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Interventions</label>
                <Textarea
                  data-testid="paste-interventions"
                  value={interventionsText}
                  onChange={(e) => setInterventionsText(e.target.value)}
                  rows={3}
                  placeholder="Paste the interventions here — one per line."
                  className="mt-1.5 bg-white border-[#DCE5DF] text-sm leading-relaxed resize-y"
                />
              </div>
            </div>
          </div>

          <label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">
            What happened in the session
          </label>

          <Dialog open={saveClientOpen} onOpenChange={setSaveClientOpen}>
            <DialogContent data-testid="save-client-dialog" className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display">Save as a client</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-[#7A8C82] -mt-1">
                We'll save these goals and interventions to this client so you can just pick them from the dropdown next time — no re-pasting.
              </p>
              <div className="space-y-3 mt-2">
                <div>
                  <label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Client name</label>
                  <Input
                    data-testid="new-client-name"
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    placeholder="e.g. James Carter"
                    className="mt-1.5 bg-[#F9F9F6] border-[#E2DFD8]"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Member / Medicaid ID (optional)</label>
                  <Input
                    data-testid="new-client-id"
                    value={newClientId}
                    onChange={(e) => setNewClientId(e.target.value)}
                    placeholder="e.g. MCD-123456"
                    className="mt-1.5 bg-[#F9F9F6] border-[#E2DFD8]"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  data-testid="confirm-save-client-btn"
                  onClick={saveAsClient}
                  disabled={savingClient}
                  className="rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white"
                >
                  {savingClient ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>) : (<><UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save client</>)}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {inputMode === "guided" ? (
            <div className="mt-2">
              <GuidedNoteInput consumerId={consumerId} answers={guided} setAnswers={setGuided} planned={plannedUsed} setPlanned={setPlannedUsed} onLastNote={setLastNote} disabled={generating} />
            </div>
          ) : (
          <Textarea
            data-testid="raw-input"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={5}
            placeholder="e.g. Met with James today at his apartment for about an hour. Worked on his goal of managing his money. We practiced making a grocery budget and he wrote out a shopping list. He stayed focused the whole time and did most of it himself. Next time we'll go over paying bills online."
            className="mt-2 bg-[#F9F9F6] border-[#E2DFD8] text-base leading-relaxed resize-y"
          />
          )}
          <Button
            data-testid="generate-note-btn"
            onClick={generate}
            disabled={generating}
            className="mt-4 w-full sm:w-auto h-12 px-8 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-base font-medium shadow-[0_8px_16px_rgba(74,107,83,0.25)] transition-colors"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Writing your note…
              </>
            ) : (
              <>
                <Wand2 className="w-5 h-5 mr-2" strokeWidth={1.5} /> Create My Note
              </>
            )}
          </Button>
        </div>
      </div>

      {/* output */}
      <div className="mt-8">
        {generating && !structured && (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-14 rounded-xl shimmer" />
            ))}
          </div>
        )}
        {structured && (
          <div className="bg-white border border-[#E2DFD8] rounded-2xl shadow-sm px-6 lg:px-8 pt-0 pb-8 overflow-hidden">
            <NoteEditor
              structured={structured}
              setStructured={(u) => {
                setStructured(u);
                setSaved(false);
              }}
              showTimestamp={showTimestamp}
              setShowTimestamp={setShowTimestamp}
              createdAt={createdAt}
              onSave={save}
              saving={saving}
              saved={saved}
              signature={signature}
              setSignature={(s) => { setSignature(s); setSaved(false); }}
              staffName={user?.name}
              staffCredential={user?.credentials}
              weekOf={weekOf}
              alignment={alignment}
              repeat={repeat}
              onRefreshInterventions={refreshInterventions}
              refreshing={refreshing}
            />
          </div>
        )}
        {!structured && !generating && (
          <div className="flex flex-col items-center text-center py-16 text-[#7A8C82]">
            <Sparkles className="w-8 h-8 mb-3 text-[#D4A373]" strokeWidth={1.5} />
            <p className="text-sm max-w-xs leading-relaxed">
              Your clean, billable PSR note will appear here after you tap
              "Create My Note."
            </p>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen grain">
      <ChangePasswordDialog />
      {/* header */}
      <header className="sticky top-0 z-40 bg-[#F9F9F6]/85 backdrop-blur-xl backdrop-saturate-150 border-b border-[#E2DFD8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[73px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#4A6B53] flex items-center justify-center shadow-[0_6px_12px_rgba(74,107,83,0.3)]">
              <Mic className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-display font-extrabold text-lg tracking-tight text-[#121814]">
              PSR Notes AI
            </span>
            <span
              data-testid="role-badge"
              className="ml-1 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md text-white"
              style={{ backgroundColor: roleMeta.color }}
            >
              {roleMeta.label}
            </span>
          </div>
          <div className="flex items-center gap-4">
            {canWrite && <NoteGuide />}
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-[#121814] leading-tight">{user?.name}</p>
              <p className="text-xs text-[#7A8C82] leading-tight">{user?.credentials || user?.email}</p>
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="w-9 h-9 rounded-full bg-white border border-[#E2DFD8] flex items-center justify-center text-[#7A8C82] hover:text-[#C26D5C] hover:border-[#C26D5C] transition-colors"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Compliance: full-width oversight + consumer management */}
      {!canWrite && canOversee && (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="inline-flex flex-wrap p-1 mb-6 rounded-full bg-[#EAE8E3] border border-[#E2DFD8]">
            <button
              data-testid="compliance-review-tab"
              onClick={() => setView("review")}
              className={`flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                view === "review" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"
              }`}
            >
              <ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> Compliance Review
            </button>
            <button
              data-testid="compliance-consumers-tab"
              onClick={() => setView("consumers")}
              className={`flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                view === "consumers" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"
              }`}
            >
              <FileHeart className="w-4 h-4" strokeWidth={1.5} /> Consumers & PCPs
            </button>
            <button
              data-testid="compliance-library-tab"
              onClick={() => setView("library")}
              className={`flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                view === "library" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"
              }`}
            >
              <Library className="w-4 h-4" strokeWidth={1.5} /> Library
            </button>
            <button
              data-testid="compliance-renewals-tab"
              onClick={() => setView("renewals")}
              className={`flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                view === "renewals" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"
              }`}
            >
              <CalendarClock className="w-4 h-4" strokeWidth={1.5} /> Renewals
            </button>
          </div>
          {view === "consumers" ? <ConsumerManager /> : view === "library" ? <LibraryManager /> : view === "renewals" ? <RenewalsView /> : <OversightView mode="compliance" />}
        </div>
      )}

      {/* Writer roles */}
      {canWrite && (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4 lg:col-span-3">
            <HistorySidebar
              notes={notes}
              activeId={activeId}
              onSelect={selectNote}
              onNew={newNote}
              onDelete={deleteNote}
            />
          </div>

          <main className="col-span-12 md:col-span-8 lg:col-span-9">
            {/* writer view switch */}
            <div className="inline-flex flex-wrap p-1 mb-6 rounded-full bg-[#EAE8E3] border border-[#E2DFD8]">
              <button
                data-testid="view-dashboard-tab"
                onClick={() => setView("dashboard")}
                className={`flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                  view === "dashboard" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"
                }`}
              >
                <LayoutDashboard className="w-4 h-4" strokeWidth={1.5} /> {canOversee ? "Dashboard" : "My Consumers"}
              </button>
              {canOversee && (
                <button
                  data-testid="view-summary-tab"
                  onClick={() => setView("summary")}
                  className={`flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                    view === "summary" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"
                  }`}
                >
                  <BarChart3 className="w-4 h-4" strokeWidth={1.5} /> Summary
                </button>
              )}
              <button
                data-testid="view-write-tab"
                onClick={() => setView("write")}
                className={`flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                  view === "write" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"
                }`}
              >
                <PenLine className="w-4 h-4" strokeWidth={1.5} /> Write Note
              </button>
              <button
                data-testid="view-weekly-tab"
                onClick={() => setView("weekly")}
                className={`flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                  view === "weekly" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"
                }`}
              >
                <CalendarDays className="w-4 h-4" strokeWidth={1.5} /> Weekly Note
              </button>
              {canOversee && (
                <button
                  data-testid="view-pcp-tab"
                  onClick={() => setView("pcp")}
                  className={`flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                    view === "pcp" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"
                  }`}
                >
                  <FilePlus2 className="w-4 h-4" strokeWidth={1.5} /> Create PCP
                </button>
              )}
              {canOversee && (
                <button
                  data-testid="view-consumers-tab"
                  onClick={() => setView("consumers")}
                  className={`flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                    view === "consumers" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"
                  }`}
                >
                  <FileHeart className="w-4 h-4" strokeWidth={1.5} /> Consumers
                </button>
              )}
              {canOversee && (
                <button
                  data-testid="view-renewals-tab"
                  onClick={() => setView("renewals")}
                  className={`flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                    view === "renewals" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"
                  }`}
                >
                  <CalendarClock className="w-4 h-4" strokeWidth={1.5} /> Renewals
                </button>
              )}
              {canOversee && (
                <button
                  data-testid="view-team-tab"
                  onClick={() => setView("team")}
                  className={`flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                    view === "team" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"
                  }`}
                >
                  <Users className="w-4 h-4" strokeWidth={1.5} /> Team Notes
                </button>
              )}
            </div>

            {view === "dashboard" ? (
              canOversee ? (
                <QpDashboard onGoConsumers={() => setView("consumers")} onGoPcp={() => setView("pcp")} onWriteNote={startNoteFor} />
              ) : (
                <StaffDashboard userName={user?.name} onWriteNote={startNoteFor} onQuickNote={(c) => startNoteFor(c)} />
              )
            ) : view === "weekly" ? (
              <WeeklyBuilder consumers={consumers} onCompiled={compiledWeekly} />
            ) : canOversee && view === "summary" ? (
              <QpSummary onGoTeam={() => setView("team")} onGoRenewals={() => setView("renewals")} />
            ) : canOversee && view === "consumers" ? (
              <ConsumerManager />
            ) : canOversee && view === "team" ? (
              <OversightView mode="qp" canReview />
            ) : canOversee && view === "renewals" ? (
              <RenewalsView />
            ) : canOversee && view === "pcp" ? (
              <PcpCreator onSaved={loadConsumers} />
            ) : (
              writingUI
            )}
          </main>
        </div>
      )}
    </div>
  );
}
